import React, { useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  withSpring,
  withDelay,
  interpolate,
  Easing,
} from 'react-native-reanimated';
import Icon from 'react-native-vector-icons/Ionicons';

const ORANGE = '#FF8C42';
const GREEN  = '#10B981';

/**
 * Props:
 *  visible     – bool  — show the pill (generation running)
 *  completed   – bool  — show completion state briefly
 *  pct         – 0-100
 *  readyCount  – number
 *  totalCount  – number
 *  onPress     – () => void
 *  isDark      – bool
 */
export default function GenerationProgressPill({
  visible,
  completed,
  pct,
  readyCount,
  totalCount,
  onPress,
  isDark,
}) {
  const spinValue = useSharedValue(0);
  const breathe   = useSharedValue(0);
  const slideX    = useSharedValue(160);
  const dotOpac   = useSharedValue(1);

  const show = visible || completed;

  useEffect(() => {
    if (show) {
      slideX.value = withSpring(0, { damping: 16, stiffness: 140 });
      if (visible) {
        spinValue.value = withRepeat(
          withTiming(360, { duration: 1500, easing: Easing.linear }), -1
        );
        breathe.value = withRepeat(
          withSequence(
            withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.sin) }),
            withTiming(0, { duration: 1200, easing: Easing.inOut(Easing.sin) })
          ), -1
        );
        dotOpac.value = withRepeat(
          withSequence(
            withTiming(0.15, { duration: 600 }),
            withTiming(1,    { duration: 600 })
          ), -1
        );
      }
    } else {
      // Delay exit so user sees the "done" state
      slideX.value = withDelay(200, withTiming(160, { duration: 280, easing: Easing.in(Easing.quad) }));
    }
  }, [show, visible]);

  const animWrap = useAnimatedStyle(() => ({
    transform: [
      { translateX: slideX.value },
      { scale: visible ? interpolate(breathe.value, [0, 1], [0.984, 1.016]) : 1 },
    ],
  }));

  const spinStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${spinValue.value}deg` }],
  }));

  const dotStyle = useAnimatedStyle(() => ({
    opacity: dotOpac.value,
    transform: [{ scale: interpolate(dotOpac.value, [0.15, 1], [0.7, 1.15]) }],
  }));

  if (!show) return null;

  const clampedPct = completed ? 100 : Math.min(100, Math.max(0, Math.round(pct || 0)));

  // position: 'fixed' on web so it stays pinned while scrolling
  const positionStyle = Platform.OS === 'web'
    ? { position: 'fixed', bottom: 28, right: 24, zIndex: 9999 }
    : { position: 'absolute', bottom: 28, right: 20, zIndex: 999 };

  // Shadow must sit on the element that also has border-radius so it clips correctly
  const pillShadow = Platform.OS === 'web'
    ? { boxShadow: completed
        ? '0 4px 20px rgba(16,185,129,0.45)'
        : '0 4px 20px rgba(255,140,66,0.40)' }
    : {
        shadowColor: completed ? GREEN : ORANGE,
        shadowOffset: { width: 0, height: 5 },
        shadowOpacity: 0.42,
        shadowRadius: 14,
        elevation: 10,
      };

  const borderColor  = completed
    ? (isDark ? 'rgba(16,185,129,0.55)' : 'rgba(16,185,129,0.5)')
    : (isDark ? 'rgba(99,102,241,0.55)'  : 'rgba(255,140,66,0.5)');

  return (
    // Transparent wrapper — no background, just handles fixed/absolute position
    <View style={[positionStyle, { backgroundColor: 'transparent' }]}>
      <Animated.View style={[animWrap, { backgroundColor: 'transparent' }]}>
        {/*
          Shadow + border live here, with the same borderRadius as the pill,
          so the glow follows the pill shape instead of rendering as a rectangle.
        */}
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={0.78}
          style={[
            s.shell,
            { borderColor },
            pillShadow,
          ]}
        >
          {/* Pill body */}
          <View style={[s.pill, isDark ? s.pillDark : s.pillLight]}>

            {/* ── Left: spinner (running) or check (done) ── */}
            <View style={s.iconZone}>
              {completed ? (
                <View style={[s.doneRing, { borderColor: GREEN }]}>
                  <Icon name="checkmark" size={11} color={GREEN} />
                </View>
              ) : (
                <>
                  <Animated.View style={[s.spinArc, spinStyle]} />
                  <View style={s.spinCore}>
                    <Icon name="sparkles" size={8} color={ORANGE} />
                  </View>
                </>
              )}
            </View>

            {/* ── Percentage ── */}
            <Text style={[s.pct, isDark ? s.pctDark : s.pctLight, completed && { color: GREEN }]}>
              {clampedPct}
              <Text style={s.pctSuffix}>%</Text>
            </Text>

            {/* ── Divider ── */}
            <View style={[s.sep, isDark ? s.sepDark : s.sepLight]} />

            {/* ── Count / Done label ── */}
            {completed ? (
              <Text style={[s.doneLabel, { color: GREEN }]}>Done!</Text>
            ) : (
              <View>
                <Text style={s.countLine}>
                  <Text style={[s.countReady, { color: ORANGE }]}>{readyCount}</Text>
                  <Text style={[s.countDen, isDark ? s.countDenDark : s.countDenLight]}>/{totalCount}</Text>
                </Text>
                <Text style={[s.countCaption, isDark ? s.captionDark : s.captionLight]}>READY</Text>
              </View>
            )}

            {/* ── Live dot (only while running) ── */}
            {!completed && (
              <Animated.View style={[s.liveDot, dotStyle]} />
            )}
          </View>

          {/* ── Progress track (bottom edge of pill) ── */}
          <View style={[s.trackBase, isDark ? s.trackBaseDark : s.trackBaseLight]}>
            <View style={[
              s.trackFill,
              { width: `${clampedPct}%`, backgroundColor: completed ? GREEN : ORANGE },
            ]} />
          </View>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
}

const s = StyleSheet.create({
  // Outer shell: carries the border + shadow + border-radius
  shell: {
    borderRadius: 30,
    borderWidth: 1.5,
    overflow: 'hidden', // clips the pill + track cleanly inside the rounded shell
  },

  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 9,
  },
  pillDark:  { backgroundColor: '#07080f' },
  pillLight: { backgroundColor: '#ffffff' },

  // ── Spinner ──────────────────────────────────────
  iconZone: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinArc: {
    position: 'absolute',
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2.5,
    borderTopColor:    ORANGE,
    borderRightColor:  ORANGE,
    borderBottomColor: 'rgba(255,140,66,0.12)',
    borderLeftColor:   'rgba(255,140,66,0.12)',
  },
  spinCore: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Completion ring
  doneRing: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Percentage ────────────────────────────────────
  pct: {
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.5,
    fontVariant: ['tabular-nums'],
  },
  pctDark:  { color: '#ecedff' },
  pctLight: { color: '#08091a' },
  pctSuffix: {
    fontSize: 11,
    fontWeight: '600',
    opacity: 0.5,
  },

  // ── Divider ───────────────────────────────────────
  sep:      { width: 1, height: 22 },
  sepDark:  { backgroundColor: 'rgba(255,255,255,0.1)' },
  sepLight: { backgroundColor: 'rgba(0,0,0,0.1)' },

  // ── Count ─────────────────────────────────────────
  countLine:  { fontSize: 13, fontWeight: '700' },
  countReady: { fontSize: 13, fontWeight: '700' },
  countDen:   { fontSize: 11, fontWeight: '500' },
  countDenDark:  { color: 'rgba(255,255,255,0.2)' },
  countDenLight: { color: '#c0c0c0' },
  countCaption:  { fontSize: 7.5, fontWeight: '800', letterSpacing: 0.9, marginTop: 1 },
  captionDark:   { color: 'rgba(255,255,255,0.2)' },
  captionLight:  { color: '#bbb' },

  // Done label
  doneLabel: { fontSize: 14, fontWeight: '800', letterSpacing: -0.2 },

  // ── Live dot ──────────────────────────────────────
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: GREEN,
  },

  // ── Progress track ────────────────────────────────
  trackBase:      { height: 3 },
  trackBaseDark:  { backgroundColor: 'rgba(255,255,255,0.05)' },
  trackBaseLight: { backgroundColor: 'rgba(0,0,0,0.06)' },
  trackFill: {
    height: '100%',
    borderTopRightRadius: 1.5,
    borderBottomRightRadius: 1.5,
  },
});
